// The canvas engine: evaluates all ten canvases in order, so a change upstream flows downstream.
// Pure JS (no React, no SDK), so it runs in Node for tests and in the browser for the app and the PDF.
import { has, key, S, hashOf, sortGaps, ledgerTop } from './helpers.js';

const DERIVED = new Set(['calc']);
export const isDerived = (col) => DERIVED.has(col.kind);

// ---------------------------------------------------------------- rows of a table block
// user-managed rows: rows.min, rows.max, rows.start (how many show first), rows.label ('Stage')
// fixed rows: rows.fixed = [{ id, label, sub? }]
// derived rows: rows.from = (up, v) => [{ id, label, sub? }]   (for example the five candidates from Canvas 2)
export function tableRows(block, v, up) {
  const r = block.rows || {};
  if (r.fixed) return r.fixed.map((x, i) => ({ id: x.id, n: i + 1, label: x.label, sub: x.sub, fixed: true }));
  if (r.from) {
    return (r.from(up, v) || []).map((x, i) => ({ id: x.id, n: i + 1, label: x.label, sub: x.sub, fixed: true }));
  }
  const max = r.max || 5;
  const min = r.min ?? 1;
  const start = r.start ?? Math.max(min, 1);
  const colIds = block.columns.filter((c) => !isDerived(c)).map((c) => c.id);
  let last = 0;
  for (let n = 1; n <= max; n += 1) {
    if (colIds.some((c) => has(v[key(block.id, `r${n}`, c)]))) last = n;
  }
  const revealed = parseInt(v[`${block.id}._n`] || '0', 10) || 0;
  const count = Math.min(max, Math.max(start, last, revealed));
  return Array.from({ length: count }, (_, i) => ({
    id: `r${i + 1}`,
    n: i + 1,
    label: `${r.label || 'Row'} ${i + 1}`,
    fixed: false,
  }));
}

// ---------------------------------------------------------------- walking a definition
export function eachBlock(def, fn) {
  def.steps.forEach((step, si) => step.blocks.forEach((block, bi) => fn(block, step, si, bi)));
}

const reqOf = (f, ctx) => (typeof f.required === 'function' ? !!f.required(ctx) : !!f.required);

// the required cells that are still empty, in reading order: [{ step, key, label }]
export function missingFor(def, v, up, d) {
  const out = [];
  eachBlock(def, (block, step) => {
    if (block.type === 'fields') {
      block.fields.forEach((f) => {
        if (isDerived(f)) return;
        if (reqOf(f, { v, up, d, row: null }) && !has(v[f.id])) out.push({ step: step.id, key: f.id, label: f.label });
      });
    }
    if (block.type === 'table') {
      const list = tableRows(block, v, up);
      const min = block.rows?.min ?? 0;
      list.forEach((row, i) => {
        const cells = {};
        block.columns.forEach((c) => {
          cells[c.id] = String(v[key(block.id, row.id, c.id)] ?? '').trim();
        });
        const touched = block.columns.some((c) => !isDerived(c) && cells[c.id] !== '');
        const must = row.fixed ? true : i < min || touched;
        if (!must) return;
        block.columns.forEach((c) => {
          if (isDerived(c)) return;
          if (reqOf(c, { v, up, d, row: cells, rowInfo: row }) && !has(cells[c.id])) {
            out.push({ step: step.id, key: key(block.id, row.id, c.id), label: `${block.title || block.id}, ${row.label}: ${c.label}` });
          }
        });
      });
    }
  });
  return out;
}

// how many required cells there are in all (for the progress meter)
export function requiredTotal(def, v, up, d) {
  let total = 0;
  eachBlock(def, (block) => {
    if (block.type === 'fields') {
      block.fields.forEach((f) => {
        if (!isDerived(f) && reqOf(f, { v, up, d, row: null })) total += 1;
      });
    }
    if (block.type === 'table') {
      const list = tableRows(block, v, up);
      const min = block.rows?.min ?? 0;
      list.forEach((row, i) => {
        const cells = {};
        block.columns.forEach((c) => {
          cells[c.id] = String(v[key(block.id, row.id, c.id)] ?? '').trim();
        });
        const touched = block.columns.some((c) => !isDerived(c) && cells[c.id] !== '');
        if (!(row.fixed || i < min || touched)) return;
        block.columns.forEach((c) => {
          if (!isDerived(c) && reqOf(c, { v, up, d, row: cells, rowInfo: row })) total += 1;
        });
      });
    }
  });
  return total;
}

// a canvas counts as started when the learner has typed anything (hidden keys start with an underscore)
export const hasAnswers = (v) => Object.keys(v || {}).some((k) => !k.startsWith('_') && has(v[k]));

// ---------------------------------------------------------------- what a canvas pulled (so we can notice when it changes)
export function pullValues(def, v, up) {
  const out = {};
  eachBlock(def, (block) => {
    if (block.type === 'pull') {
      block.items.forEach((it) => {
        let val = null;
        try {
          val = it.get(up, v);
        } catch (e) {
          val = null;
        }
        out[`${block.id || 'pull'}.${it.id}`] = val === undefined ? null : val;
      });
    }
    if (block.type === 'table' && block.rows?.from) {
      try {
        out[`${block.id}.rows`] = block.rows.from(up, v);
      } catch (e) {
        out[`${block.id}.rows`] = null;
      }
    }
  });
  return out;
}

// ---------------------------------------------------------------- evaluate everything, in order
// entries: { [canvasNo]: { v: {answers}, status: 'In progress' | 'Confirmed' } }  (missing canvases are blank)
// returns { states: { [no]: state }, agg: { gaps, ledger, ledgerTop } }
// state: { no, def, v, d, status, started, confirmed, missing, total, completion, sig, stale, pulled }
//   d (what derive() returned): { calc, results, facts, evidence, warnings }
export function evaluateAll(entries, defs) {
  const rawGaps = [];
  const rawLedger = [];
  const up = { agg: { gaps: [], ledger: [], ledgerTop: [] } };
  const refreshAgg = () => {
    up.agg = { gaps: sortGaps(rawGaps), ledger: [...rawLedger], ledgerTop: ledgerTop(rawLedger, 3) };
  };
  const states = {};
  defs.forEach((def) => {
    const e = entries?.[def.no] || {};
    const v = e.v || {};
    let d = {};
    try {
      d = def.derive ? def.derive(v, up) || {} : {};
    } catch (err) {
      d = { error: String(err && err.message ? err.message : err) };
    }
    d.calc = d.calc || {};
    d.results = d.results || {};
    d.facts = d.facts || {};
    d.evidence = d.evidence || [];
    d.warnings = d.warnings || [];
    d.blockers = d.blockers || [];
    const status = e.status || (hasAnswers(v) ? 'In progress' : 'Not started');
    const state = { no: def.no, def, v, d, status, started: hasAnswers(v), confirmed: status === 'Confirmed' };
    up[def.no] = state;
    const missing = missingFor(def, v, up, d);
    const total = requiredTotal(def, v, up, d);
    state.missing = missing;
    state.blockers = d.blockers;
    state.canConfirm = missing.length === 0 && d.blockers.length === 0 && !d.error;
    state.total = total;
    state.completion = total === 0 ? (state.started ? 100 : 0) : Math.round((100 * (total - missing.length)) / total);
    state.pulled = pullValues(def, v, up);
    state.sig = hashOf(state.pulled);
    state.stale = state.confirmed && has(v._sig) && v._sig !== state.sig;
    // feed the lists that later canvases pull from
    d.evidence.forEach((g) => rawGaps.push({ ...g, canvas: def.no }));
    const so = def.signoff;
    if (so && so.ledger !== false && has(v['so.objection'])) {
      rawLedger.push({
        canvas: def.no,
        where: `Canvas ${def.no}`,
        sceptic: S(v, 'so.reviewer') || def.sceptic,
        objection: S(v, 'so.objection'),
        evidence: S(v, 'so.evidence'),
        owner: S(v, 'so.owner'),
        status: S(v, 'so.status') || 'Open',
        star: S(v, 'so.star') === 'Yes',
      });
    }
    refreshAgg();
    states[def.no] = state;
  });
  return { states, agg: up.agg };
}

// the starting answers of a canvas: every `default` on a field or a table cell (a value, or a function of the row)
export function defaultAnswers(def) {
  const out = {};
  const val = (dflt, row) => (typeof dflt === 'function' ? dflt(row) : dflt);
  eachBlock(def, (block) => {
    if (block.type === 'fields') {
      block.fields.forEach((f) => {
        if (f.default !== undefined && f.default !== null) out[f.id] = String(val(f.default, null));
      });
    }
    if (block.type === 'table' && block.rows?.fixed) {
      block.rows.fixed.forEach((row) => {
        block.columns.forEach((c) => {
          if (c.default !== undefined && c.default !== null) out[key(block.id, row.id, c.id)] = String(val(c.default, row));
        });
      });
    }
  });
  return out;
}

// the canvases a given canvas depends on that are not confirmed yet
export function unconfirmedUpstream(def, states) {
  return (def.pullsFrom || [])
    .filter((n) => typeof n === 'number')
    .filter((n) => !states[n]?.confirmed)
    .sort((a, b) => a - b);
}

// can the whole pack be exported? every canvas confirmed and none stale
export function packReady(states) {
  const all = Object.values(states);
  return all.length > 0 && all.every((s) => s.confirmed && !s.stale);
}
